/**
 * Cities Section Component
 * Smart component - wires up Redux actions to dumb components
 * Only accessible to super admins
 */

import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { useSelector, useDispatch, useTranslation, useIsRTL, getLocalizedValue, exportToXlsx } from '@ufc/core'
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  PaginationControls,
  cn,
  Plus,
  Search,
  Building2,
  ShieldAlert,
  MoreHorizontal,
  LayoutGrid,
  LayoutList,
  Download,
} from '@ufc/uikit'

import {
  selectCities,
  selectCityStats,
  selectCitiesLoading,
  selectCitiesError,
  selectCitiesFilter,
  selectCitiesPagination,
  selectAvailableRegions,
  selectAvailableCountries,
  selectSelectedCity,
} from '../../store/cities.selectors'
import { citiesActions } from '../../store/cities.slice'
import type { CityListItem, City, CreateCityRequest, UpdateCityRequest } from '../../domain/types'
import { citiesApi } from '../../infrastructure/api/cities-api'
import { CityCard } from '../cards'
import type { CityFormSubmitData } from '../drawers'
const CityFormDrawer = lazy(() => import('../drawers/city-form-drawer').then(m => ({ default: m.CityFormDrawer })))
const CityDetailDrawer = lazy(() => import('../drawers/city-detail-drawer').then(m => ({ default: m.CityDetailDrawer })))
const DeleteCityDialog = lazy(() => import('../modals').then(m => ({ default: m.DeleteCityDialog })))

// Import auth selectors for admin/role checks
import { selectIsAdmin, selectUserRoleType, isLocationConfigReadOnlyRole } from '@ufc/domains/auth'

// ============================================================================
// Component
// ============================================================================

export function CitiesSection() {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const isRTL = useIsRTL()
  const isArabic = i18n.language === 'ar' // Keep for content selection (Arabic text vs English)

  // Redux state
  const cities = useSelector(selectCities)
  const stats = useSelector(selectCityStats)
  const loading = useSelector(selectCitiesLoading)
  const error = useSelector(selectCitiesError)
  const filter = useSelector(selectCitiesFilter)
  const pagination = useSelector(selectCitiesPagination)
  const regions = useSelector(selectAvailableRegions)
  const countries = useSelector(selectAvailableCountries)
  const selectedCity = useSelector(selectSelectedCity)
  const isAdmin = useSelector(selectIsAdmin)
  const roleType = useSelector(selectUserRoleType)
  const isReadOnly = isLocationConfigReadOnlyRole(roleType)

  // Local state
  const [searchValue, setSearchValue] = useState(filter.search || '')
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [cityToEdit, setCityToEdit] = useState<City | null>(null)
  const [cityToDelete, setCityToDelete] = useState<CityListItem | null>(null)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table')
  const [isExporting, setIsExporting] = useState(false)

  // Fetch data on mount
  useEffect(() => {
    dispatch(citiesActions.fetchCities())
    dispatch(citiesActions.fetchCityStats())
    dispatch(citiesActions.fetchAvailableRegions())
    dispatch(citiesActions.fetchAvailableCountries())
  }, [dispatch])

  useEffect(() => {
    setSearchValue(filter.search || '')
  }, [filter.search])

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value)
      dispatch(citiesActions.setFilter({ search: value, page: 1 }))
    },
    [dispatch]
  )

  const handleStatusFilter = useCallback(
    (status: string) => {
      const isActive = status === 'all' ? 'all' : status === 'active'
      dispatch(citiesActions.setFilter({ isActive, page: 1 }))
    },
    [dispatch]
  )

  const handleRegionFilter = useCallback(
    (regionId: string) => {
      dispatch(citiesActions.setFilter({ regionId: regionId === 'all' ? undefined : regionId, page: 1 }))
    },
    [dispatch]
  )

  const handleAddClick = useCallback(() => {
    setCityToEdit(null)
    setIsFormDrawerOpen(true)
  }, [])

  const handleCardSelect = useCallback(
    (city: CityListItem) => {
      dispatch(citiesActions.fetchCityDetail(city.documentId))
      setIsDetailDrawerOpen(true)
    },
    [dispatch]
  )

  const handleCardSettings = useCallback(
    (city: CityListItem) => {
      dispatch(citiesActions.fetchCityDetail(city.documentId))
      setIsFormDrawerOpen(true)
    },
    [dispatch]
  )

  // Set cityToEdit when selectedCity changes (after fetch)
  useEffect(() => {
    if (selectedCity && isFormDrawerOpen) {
      setCityToEdit(selectedCity)
    }
  }, [selectedCity, isFormDrawerOpen])

  const handleEditFromDetail = useCallback((city: City) => {
    setCityToEdit(city)
    setIsDetailDrawerOpen(false)
    setIsFormDrawerOpen(true)
  }, [])

  const handleDeleteFromDetail = useCallback((city: City) => {
    // Convert City to CityListItem for delete dialog
    const cityListItem: CityListItem = {
      id: city.id,
      documentId: city.documentId,
      code: city.code,
      name: city.name,
      stateProvince: city.stateProvince,
      isActive: city.isActive,
      regionId: city.region?.id,
      regionCode: city.region?.code,
      regionName: city.region?.name,
      countryId: city.country?.id,
      countryCode: city.country?.code,
      countryName: city.country?.name,
      createdAt: city.createdAt,
      updatedAt: city.updatedAt,
    }
    setCityToDelete(cityListItem)
    setIsDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (cityToDelete) {
      dispatch(citiesActions.deleteCity(cityToDelete.documentId))
      setIsDeleteDialogOpen(false)
      setIsDetailDrawerOpen(false)
      setCityToDelete(null)
    }
  }, [dispatch, cityToDelete])

  const handleFormSubmit = useCallback(
    (data: CityFormSubmitData) => {
      if (cityToEdit) {
        const updateRequest: UpdateCityRequest = {
          name: data.name,
          stateProvince: data.stateProvince,
          salesDistrict: data.salesDistrict,
          isActive: data.isActive,
          countryId: data.countryId,
        }
        dispatch(
          citiesActions.updateCity({
            documentId: cityToEdit.documentId,
            data: updateRequest,
          })
        )
      } else {
        const createRequest: CreateCityRequest = {
          code: data.code,
          name: data.name,
          stateProvince: data.stateProvince,
          salesDistrict: data.salesDistrict,
          isActive: data.isActive,
          countryId: data.countryId,
        }
        dispatch(citiesActions.createCity(createRequest))
      }
      setIsFormDrawerOpen(false)
      setCityToEdit(null)
    },
    [dispatch, cityToEdit]
  )

  const handleFormDrawerClose = useCallback(
    (open: boolean) => {
      setIsFormDrawerOpen(open)
      if (!open) {
        setCityToEdit(null)
        dispatch(citiesActions.setSelectedCity(null))
      }
    },
    [dispatch]
  )

  const handleDetailDrawerClose = useCallback(
    (open: boolean) => {
      setIsDetailDrawerOpen(open)
      if (!open) {
        dispatch(citiesActions.setSelectedCity(null))
      }
    },
    [dispatch]
  )

  const handleRetry = useCallback(() => {
    dispatch(citiesActions.fetchCities())
  }, [dispatch])

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(citiesActions.setFilter({ page }))
    },
    [dispatch]
  )

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      dispatch(citiesActions.setFilter({ pageSize, page: 1 }))
    },
    [dispatch]
  )

  const handleExport = useCallback(async () => {
    setIsExporting(true)
    try {
      const rows = await citiesApi.exportAll()
      exportToXlsx({
        fileName: 'cities',
        sheetName: 'Cities',
        columns: [
          { header: 'Code', key: 'Code' },
          { header: 'Name (English)', key: 'Name (English)' },
          { header: 'Name (Arabic)', key: 'Name (Arabic)' },
          { header: 'Country', key: 'Country' },
          { header: 'State/Province (English)', key: 'State/Province (English)' },
          { header: 'State/Province (Arabic)', key: 'State/Province (Arabic)' },
          { header: 'Sales District', key: 'Sales District' },
          { header: 'Region', key: 'Region' },
          { header: 'Status', key: 'Status' },
        ],
        rows,
      })
    } catch {
      // silent fail
    } finally {
      setIsExporting(false)
    }
  }, [])

  // ============================================================================
  // Render
  // ============================================================================

  // Access denied - not an admin (IT has read-only access)
  if (!isAdmin && !isReadOnly) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-amber-500 mb-4">
          <ShieldAlert className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('common.accessDenied', 'Access Denied')}
        </h3>
        <p className="text-muted-foreground max-w-md">
          {isArabic
            ? 'إدارة المدن مقيدة لمسؤولي النظام. يرجى التواصل مع المسؤول إذا كنت بحاجة إلى الوصول.'
            : 'City management is restricted to system administrators. Please contact your administrator if you need access.'}
        </p>
      </div>
    )
  }

  // Loading state
  if (loading && cities.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  // Error state
  if (error && cities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-destructive mb-4">
          <Building2 className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('common.error')}</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={handleRetry}>{t('common.retry')}</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={cn('flex items-center justify-between flex-wrap gap-4')}>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('cities.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isArabic
              ? `${stats.total} مدينة • ${stats.active} نشطة • ${stats.inactive} غير نشطة`
              : `${stats.total} cities • ${stats.active} active • ${stats.inactive} inactive`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={isExporting}>
            <Download className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
            {isExporting ? t('common.exporting', 'Exporting...') : t('common.export', 'Export')}
          </Button>
          {!isReadOnly && (
            <Button onClick={handleAddClick}>
              <Plus className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
              {t('cities.addCity')}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('cities.totalCities')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('cities.activeCities')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('cities.inactiveCities')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{stats.inactive}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className={cn('flex flex-col sm:flex-row gap-4')}>
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('cities.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            className="ps-10"
          />
        </div>
        <Select
          value={filter.regionId || 'all'}
          onValueChange={handleRegionFilter}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder={t('cities.filter.byRegion')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cities.filter.allRegions')}</SelectItem>
            {regions.map((region) => (
              <SelectItem key={region.documentId} value={region.documentId}>
                {region.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filter.isActive === 'all' ? 'all' : filter.isActive ? 'active' : 'inactive'}
          onValueChange={handleStatusFilter}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder={t('cities.filter.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cities.filter.all')}</SelectItem>
            <SelectItem value="active">{t('cities.filter.active')}</SelectItem>
            <SelectItem value="inactive">{t('cities.filter.inactive')}</SelectItem>
          </SelectContent>
        </Select>
        <div className="inline-flex items-center rounded-2xl border border-border/70 bg-muted/50 p-1">
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              'h-8 w-8 rounded-xl transition-colors',
              viewMode === 'table'
                ? 'bg-background text-primary shadow-sm hover:bg-background'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setViewMode('table')}
            aria-label={t('common.tableView', { defaultValue: 'Table View' })}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              'h-8 w-8 rounded-xl transition-colors',
              viewMode === 'cards'
                ? 'bg-background text-primary shadow-sm hover:bg-background'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setViewMode('cards')}
            aria-label={t('common.cardView', { defaultValue: 'Card View' })}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Cities Grid */}
      {cities.length > 0 && (
        viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {cities.map((city) => (
              <CityCard
                key={city.documentId}
                city={city}
                onSelect={handleCardSelect}
                onSettings={isReadOnly ? undefined : handleCardSettings}
              />
            ))}
          </div>
        ) : (
          <div className="glass-card rounded-2xl overflow-hidden border border-border/50">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
                    <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('cities.table.code')}</TableHead>
                    <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('cities.table.name')}</TableHead>
                    <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('cities.table.country')}</TableHead>
                    <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('cities.table.stateProvince')}</TableHead>
                    <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('cities.table.salesDistrict')}</TableHead>
                    <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider">{t('cities.table.status')}</TableHead>
                    <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider text-end w-[120px]">{t('cities.table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cities.map((city) => (
                    <TableRow
                      key={city.documentId}
                      className="cursor-pointer border-border/50 hover:bg-secondary/30 transition-colors"
                      onClick={() => handleCardSelect(city)}
                    >
                      <TableCell className="px-5 py-4 font-medium">{city.code}</TableCell>
                      <TableCell className="px-5 py-4">
                        {getLocalizedValue(city.name, i18n.language)}
                      </TableCell>
                      <TableCell className="px-5 py-4">{city.countryName || t('cities.card.noCountry')}</TableCell>
                      <TableCell className="px-5 py-4">
                        {city.stateProvince
                          ? getLocalizedValue(city.stateProvince, i18n.language)
                          : '-'}
                      </TableCell>
                      <TableCell className="px-5 py-4 font-mono text-xs text-muted-foreground">
                        {city.salesDistrict || '-'}
                      </TableCell>
                      <TableCell className="px-5 py-4">{city.isActive ? t('cities.active') : t('cities.inactive')}</TableCell>
                      <TableCell className="px-5 py-4 text-end">
                        {!isReadOnly && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 rounded-xl hover:bg-muted"
                                aria-label={t('common.actions')}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              onClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              <DropdownMenuItem
                                onSelect={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleCardSettings(city)
                                }}
                              >
                                {t('common.edit')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )
      )}

      {/* Pagination */}
      {pagination.total > 0 && (
        <PaginationControls
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.total}
          totalPages={pagination.pageCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizes={[10, 20, 50, 100]}
        />
      )}

      {/* Empty State */}
      {!loading && cities.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">
            {filter.search || filter.isActive !== 'all' || filter.regionId
              ? (isArabic ? 'لا توجد نتائج' : 'No results found')
              : t('cities.noCities')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {filter.search || filter.isActive !== 'all' || filter.regionId
              ? (isArabic ? 'جرب تغيير معايير البحث' : 'Try adjusting your search criteria')
              : t('cities.noCitiesDescription')}
          </p>
          {!filter.search && filter.isActive === 'all' && !filter.regionId && !isReadOnly && (
            <Button className="mt-4" onClick={handleAddClick}>
              <Plus className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
              {t('cities.addFirst')}
            </Button>
          )}
        </div>
      )}

      {/* Detail Drawer */}
      <Suspense fallback={null}>
        <CityDetailDrawer
          city={selectedCity}
          open={isDetailDrawerOpen}
          onOpenChange={handleDetailDrawerClose}
          onEdit={isReadOnly ? undefined : handleEditFromDetail}
          onDelete={isReadOnly ? undefined : handleDeleteFromDetail}
          isRTL={isRTL}
        />
      </Suspense>

      {/* Form Drawer */}
      <Suspense fallback={null}>
        <CityFormDrawer
          open={isFormDrawerOpen}
          onOpenChange={handleFormDrawerClose}
          city={cityToEdit}
          countries={countries}
          onSubmit={handleFormSubmit}
          isLoading={loading}
          isRTL={isRTL}
        />
      </Suspense>

      {/* Delete Confirmation Dialog */}
      {cityToDelete && (
        <Suspense fallback={null}>
          <DeleteCityDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            cityName={getLocalizedValue(cityToDelete.name, i18n.language)}
            cityCode={cityToDelete.code}
            onConfirm={handleDeleteConfirm}
            isLoading={loading}
            isRTL={isRTL}
          />
        </Suspense>
      )}
    </div>
  )
}
